import React from "react";
import logo from "../../assets/img/om-full-primary-logo.svg";
import signature from "../../assets/img/signature.jpeg";
import { getUserDetail } from "../../util/util";

export const TransactionStatement = ({ companyName = '', data = [], companyId, signatureUrl = '/signature.png' }) => {
  const user = typeof window !== 'undefined' ? getUserDetail?.() : null;

  const formatPersonName = (rawName) => {
    if (!rawName || typeof rawName !== 'string') return '';
    const formatSegment = (segment = '') => segment
      .split(/([-'])/)
      .map((part) => (part === '-' || part === "'") ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('');
    return rawName
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(formatSegment)
      .join(' ');
  };

  const derivedName = user?.full_name || user?.name || companyName || 'Valued Customer';
  const recipientName = formatPersonName(derivedName) || 'Valued Customer';

  // Normalize incoming transaction items into rows: { date, type, reference, amount }
  const rows = Array.isArray(data) ? data.map(item => {
    const date = item.payment_date || item.created_on || item.date || item.transaction_date || item.txn_date || '';
    const type = item.trans_type || item.type || item.transaction || item.description || '';
    const reference = item.receipt_number || item.reference || item.id || item.txn_ref || '';
    const amountRaw = typeof item.received !== 'undefined' ? item.received : (item.amount || item.net_premium || item.money_allocated || 0);
    const amount = Number(String(amountRaw).replace(/,/g, '')) || 0;
    return { date, type, reference: String(reference), amount };
  }) : [];

  // Pagination logic: 14 rows on first page, 18 on subsequent pages
  function paginateRows(rows, firstPageSize = 14, pageSize = 18) {
    if (rows.length <= firstPageSize) return [rows];
    const pages = [];
    pages.push(rows.slice(0, firstPageSize));
    let i = firstPageSize;
    while (i < rows.length) {
      pages.push(rows.slice(i, i + pageSize));
      i += pageSize;
    }
    return pages;
  }
  const pagedRows = paginateRows(rows);

  // Totals and period
  const totalCredits = rows.reduce((s, r) => s + (r.amount > 0 ? r.amount : 0), 0);
  const totalDebits = rows.reduce((s, r) => s + (r.amount < 0 ? Math.abs(r.amount) : 0), 0);
  const dates = rows.map(r => new Date(r.date)).filter(d => !isNaN(d.getTime()));
  const period = dates.length ? `${dates.reduce((a,b)=> a<b?a:b).toLocaleDateString('en-US',{ month: 'long', year: 'numeric' })} - ${dates.reduce((a,b)=> a>b?a:b).toLocaleDateString('en-US',{ month: 'long', year: 'numeric' })}` : '';

  const fmt = (v) => `GHS ${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  // PDF-optimized smaller padding to fit table within page width
  const headerCellBaseStyle = {
    textAlign: 'center',
    padding: '6px 4px',
    color: '#222',
    borderRight: '1px solid #888',
    fontSize: '10px'
  };

  const dataCellBaseStyle = {
    padding: '5px 4px',
    color: '#222',
    fontWeight: 500,
    borderRight: '1px solid #888',
    fontSize: '9px',
    verticalAlign: 'middle'
  };

  // Dynamic column widths based on content
  const getColumnWidthStyles = () => {
    // Analyze each column's content
    const colAnalysis = [
      { key: 'date', headerLen: 4, contentLens: rows.map(r => String(r.date || '').length) },
      { key: 'type', headerLen: 16, contentLens: rows.map(r => String(r.type || '').length) },
      { key: 'reference', headerLen: 9, contentLens: rows.map(r => String(r.reference || '').length) },
      { key: 'amount', headerLen: 6, contentLens: rows.map(r => fmt(r.amount).length) }
    ];
    
    // Calculate required width for each column
    const widths = colAnalysis.map(col => {
      const maxContent = Math.max(...col.contentLens, 0);
      const maxLen = Math.max(col.headerLen, maxContent);
      // Base 10 chars = 12%, each extra char = 0.9%, min 8%, max 35%
      return Math.max(8, Math.min(35, 12 + (maxLen * 0.9)));
    });
    
    // Normalize to 100%
    const total = widths.reduce((a, b) => a + b, 0);
    const normalized = widths.map(w => (w / total) * 100);
    
    return {
      date: { width: `${normalized[0]}%`, borderRight: '1px solid #888', textAlign: 'center', whiteSpace: 'nowrap' },
      type: { width: `${normalized[1]}%`, borderRight: '1px solid #888', textAlign: 'center', whiteSpace: 'nowrap' },
      reference: { width: `${normalized[2]}%`, borderRight: '1px solid #888', textAlign: 'center', wordBreak: 'break-all', fontSize: '8px' },
      amount: { width: `${normalized[3]}%`, borderRight: 'none', textAlign: 'center', whiteSpace: 'nowrap' }
    };
  };
  
  const columnWidthStyles = getColumnWidthStyles();

  const montserratFontCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;900&display=swap');
    .pdf-page, .pdf-page * {
      font-family: 'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
      letter-spacing: 0.02em;
    }
    .pdf-table {
      table-layout: fixed;
      width: 100%;
      max-width: 100%;
      font-size: 10px;
    }
    .pdf-table th,
    .pdf-table td {
      word-break: break-word;
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
      max-width: 100%;
    }
    /* Reference column - break at any character */
    .pdf-table td:nth-child(3),
    .pdf-table th:nth-child(3) {
      word-break: break-all !important;
      font-size: 9px !important;
    }
    /* Transaction Type column - no wrap */
    .pdf-table th.type-col,
    .pdf-table td.type-col {
      white-space: nowrap !important;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* Amount column - no wrap */
    .pdf-table td:nth-child(4),
    .pdf-table th:nth-child(4) {
      white-space: nowrap !important;
    }
    .pdf-table th {
      vertical-align: middle;
      line-height: 1.2;
      font-size: 11px !important;
    }
    .pdf-table td {
      vertical-align: middle;
      line-height: 1.3;
    }
    .pdf-bold-emphasis {
      font-weight: 700 !important;
      font-size: 16px !important;
      line-height: 30px !important;
      letter-spacing: -1px !important;
      vertical-align: middle;
    }
    .pdf-title-underline {
      font-weight: 700 !important;
      font-size: 16px !important;
      line-height: 30px !important;
      letter-spacing: -1px !important;
      vertical-align: middle;
      text-decoration: underline solid currentColor;
      text-underline-offset: 0;
      text-decoration-thickness: from-font;
    }
  `;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <style dangerouslySetInnerHTML={{ __html: montserratFontCSS }} />
      {/* Inline styles to help html2pdf/jsPDF paginate to A4 and avoid overlapping */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          .pdf-page { width: 100%; box-sizing: border-box; page-break-after: always; }
          .pdf-page:last-child { page-break-after: auto; }
          .avoid-break { page-break-inside: avoid; break-inside: avoid; -webkit-column-break-inside: avoid; }
          table { page-break-inside: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; break-inside: avoid; }
        }
        /* PDF table sizing - ensure it fits within A4 width */
        .pdf-page {
          max-width: 190mm !important;
          margin: 0 auto !important;
          padding: 10mm !important;
        }
        .pdf-table {
          table-layout: fixed !important;
          width: 100% !important;
          max-width: 100% !important;
          font-size: 10px !important;
        }
        .pdf-table th, .pdf-table td {
          padding: 6px 4px !important;
          word-break: break-word !important;
          font-size: 10px !important;
          box-sizing: border-box !important;
        }
        .pdf-table th {
          font-size: 11px !important;
          vertical-align: middle !important;
        }
        /* Reference column smaller font */
        .pdf-table th:nth-child(3), .pdf-table td:nth-child(3) {
          font-size: 9px !important;
          word-break: break-all !important;
        }
        /* Amount column nowrap */
        .pdf-table th:nth-child(4), .pdf-table td:nth-child(4) {
          white-space: nowrap !important;
        }
        .pdf-table { table-layout: fixed !important; width: 100% !important; max-width: 100% !important; }
        .pdf-table th, .pdf-table td { 
          word-break: break-word !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          vertical-align: top !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .pdf-table th { vertical-align: middle !important; line-height: 1.3 !important; }
        .pdf-table td { line-height: 1.4 !important; }
        .pdf-table th:last-child,
        .pdf-table td:last-child {
          border-right: none !important;
        }
      `}} />
  {/* Document Container (one printable PDF page block; will allow automatic breaks) */}
  <div className="bg-white shadow-md max-w-4xl mx-auto pdf-page" style={{ padding: 0 }}>
        {/* Top Green Border */}
        <div className="h-2 bg-green-600 w-full"></div>

        {/* Document Content - reduced padding for PDF fit */}
        <div className="p-4">
       
          <div
            style={{
              borderTop: "8px solid #008000",
              borderBottom: "8px solid #008000",
              padding: "10px 0 10px 0",
              textAlign: "left",
              marginBottom: 24
            }}
          >
            <img src={logo} alt="Old Mutual Logo" style={{ height: "40px" }} />
          </div>
          <div className="text-sm text-gray-500" style={{ marginBottom: 20 }}>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          <div className="mb-6" style={{ marginBottom: 24 }}>
            <div className="text-sm">Dear {recipientName},</div>
          </div>
          {/* Title */}
          <div className="mb-6">
            <div className="pdf-title-underline" style={{ color: '#222' }}>TRANSACTION STATEMENT</div>
      <div className="text-sm" style={{ marginBottom: 28 }}>Kindly find below your transaction Statement</div>
          </div>

          {/* Summary Section */}
              <div className="mb-6">
                <div className="pdf-bold-emphasis" style={{ color: '#222', marginBottom: 8 }}>SUMMARY</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Currency:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>GHS</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Total Credits:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>{fmt(totalCredits)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Total Debits:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>{fmt(totalDebits)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 28 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Period:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>{period || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Statement Table - paginated: 9 rows on first page, 14 on subsequent pages */}
          {pagedRows.map((page, pageIdx) => (
            <div
              className="mb-6"
              key={pageIdx}
              style={{
                pageBreakAfter: pageIdx < pagedRows.length - 1 ? 'always' : 'auto',
                marginTop: pageIdx === 0 ? undefined : 48 // Add 48px top margin to subsequent pages
              }}
            >
              {pageIdx === 0 && (
                <div className="pdf-bold-emphasis" style={{ color: '#222', marginBottom: 8 }}>STATEMENT</div>
              )}
              <table className="pdf-table" style={{ borderCollapse: 'collapse', fontSize: 15, background: '#fff', border: '1px solid #888' }}>
                {pageIdx === 0 && (
                  <thead>
                    <tr style={{ background: '#fff', borderBottom: '1px solid #888' }}>
                      <th className="pdf-bold-emphasis" style={{ ...headerCellBaseStyle, ...columnWidthStyles.date }}>DATE</th>
                      <th className="pdf-bold-emphasis type-col" style={{ ...headerCellBaseStyle, ...columnWidthStyles.type }}>TRANSACTION TYPE</th>
                      <th className="pdf-bold-emphasis" style={{ ...headerCellBaseStyle, ...columnWidthStyles.reference }}>REFERENCE</th>
                      <th className="pdf-bold-emphasis" style={{ ...headerCellBaseStyle, ...columnWidthStyles.amount }}>AMOUNT</th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {page.map((transaction, index) => (
                    <tr key={index + pageIdx * 14} style={{ borderBottom: '1px solid #888' }}>
                      <td style={{ ...dataCellBaseStyle, ...columnWidthStyles.date }}>{transaction.date ? new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</td>
                      <td className="type-col" style={{ ...dataCellBaseStyle, ...columnWidthStyles.type }}>{transaction.type}</td>
                      <td style={{ ...dataCellBaseStyle, ...columnWidthStyles.reference }}>{transaction.reference}</td>
                      <td style={{ ...dataCellBaseStyle, ...columnWidthStyles.amount }}>{fmt(transaction.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div style={{ height: 32 }}></div>

          {/* Footer Messages */}
          <div className="text-sm mb-6">
            <div className="mb-2">Please do not indicate to insert if further clarification is required.</div>
            <div className="mb-2">We have no benefits from you shortly.</div>
            <div>Yours faithfully.</div>
          </div>

          {/* Bottom Separator */}
          <div className="h-px bg-gray-300 my-4"></div>

          {/* Signature block - show image if available, otherwise show dotted line */}
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            {signature  ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <img src={signature } alt="signature" style={{ height: 80, objectFit: 'contain', marginBottom: 6 }} />
                 <div style={{ fontSize: 20, color: '#999', marginBottom: 0 }}>..................................................</div>
              </div>
              
            ) : (
              <div style={{ fontSize: 20, color: '#999', marginBottom: 6 }}>..................................................</div>
            )}

          {/* Footer View Note */}
          <div className="text-sm text-gray-500 mb-1">Wisdom Kwabena Donor</div>
          <div className="text-sm text-gray-500">(Head, Corporate Distribution)</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionStatement;